/* eslint-disable no-script-url */
/* eslint-disable no-undef */
import { Common } from '@stockbit/ui';
import { useState } from 'react';
import If from 'global/ErrorHandlers/If';
import QueryProvider from 'global/QueryProvider';

import { VerifyPhoneErrorType } from 'features/register/types';
import { EditProfileCard } from '../styled';
import SuccessCard from '../components/SuccessCard';
import VerifyPhone from './Verify';
import PhoneOTP from './OTP';
import NewPhone from './NewPhone';
import ProgressGuard from '../ProgressGuard';
import SelectOtpMethod from './SelectOtpMethod';
import ModalOtpLimit from '../ModalOtpLimit';

const { Flex, Iconmoon } = Common;

enum ChangePhoneStep {
  Verify = 'verify',
  Change = 'change',
  Otp = 'otp',
  SelectOtp = 'select_otp',
  Success = 'success',
}

interface ChangePhoneProps {
  profile: Record<string, any>;
  backLink?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  enableGuard?: boolean;
  backToEditForm?: () => void;
}

function ChangePhone({
  profile,
  backLink = '/profile/edit',
  backToEditForm = () => {},
}: ChangePhoneProps) {
  const [step, setStep] = useState<ChangePhoneStep>(ChangePhoneStep.Verify);
  const [isOtpLimitVisible, setIsOtpLimitVisible] = useState(false);

  const getIsStepVisible = (stepValue: ChangePhoneStep) => step === stepValue;

  const handleBack = () => {
    const backStep = {
      [ChangePhoneStep.Verify]: () => backToEditForm(),
      [ChangePhoneStep.Otp]: ChangePhoneStep.Change,
      [ChangePhoneStep.SelectOtp]: ChangePhoneStep.Change,
    };

    if (typeof backStep[step] === 'function') {
      backStep[step]();
      return;
    }
    setStep(backStep[step]);
  };

  const handleOtpLimitBack = () => {
    setStep(ChangePhoneStep.Otp);
    setIsOtpLimitVisible(false);
  };

  const handleVerifyPhoneError = (type: VerifyPhoneErrorType) => {
    if (type === VerifyPhoneErrorType.OtpLimit) {
      setIsOtpLimitVisible(true);
    }
  };

  const renderTitle = () => {
    const BACK_STEPS = [
      ChangePhoneStep.SelectOtp,
      ChangePhoneStep.Verify,
      ChangePhoneStep.Otp,
    ];
    const showBack = BACK_STEPS.includes(step);

    return (
      <Flex align="center" style={{ gap: 8 }}>
        <If condition={showBack}>
          <Iconmoon
            className="icon-arrow-left"
            onClick={handleBack}
            size="12px"
            style={{ cursor: 'pointer' }}
            data-cy="change-index-back-button"
          />
        </If>
        Ubah Nomor Hp
      </Flex>
    );
  };

  return (
    <>
      <ModalOtpLimit
        onBackClick={handleOtpLimitBack}
        visible={isOtpLimitVisible}
      />
      <EditProfileCard
        variant="profile-config"
        title={renderTitle()}
        isHideTitle={step === ChangePhoneStep.Success}
        data-cy="change-index-form"
      >
        <div id="securities-box-changePin" style={{ width: '100%' }}>
          <If condition={getIsStepVisible(ChangePhoneStep.Verify)}>
            <VerifyPhone
              onNext={() => setStep(ChangePhoneStep.Change)}
              cancelLink="javascript:void(0)"
              onClickCancel={backToEditForm}
            />
          </If>
          <If condition={getIsStepVisible(ChangePhoneStep.Change)}>
            <NewPhone
              phone={profile.phone}
              onNext={() => setStep(ChangePhoneStep.SelectOtp)}
              cancelLink="javascript:void(0)"
              onClickCancel={backToEditForm}
            />
          </If>
          <If condition={getIsStepVisible(ChangePhoneStep.SelectOtp)}>
            <SelectOtpMethod
              onNext={() => setStep(ChangePhoneStep.Otp)}
              onError={handleVerifyPhoneError}
            />
          </If>
          <If condition={getIsStepVisible(ChangePhoneStep.Otp)}>
            <PhoneOTP
              onNext={() => setStep(ChangePhoneStep.Success)}
              onResend={() => setStep(ChangePhoneStep.SelectOtp)}
            />
          </If>

          <If condition={getIsStepVisible(ChangePhoneStep.Success)}>
            <SuccessCard type="phone" backLink={backLink} />
          </If>
        </div>
      </EditProfileCard>
    </>
  );
}

function ChangePhoneWithProgressGuard(props: ChangePhoneProps) {
  const { enableGuard = false } = props;

  if (!enableGuard) {
    return <ChangePhone {...props} />;
  }

  return (
    <QueryProvider>
      <ProgressGuard>
        <ChangePhone {...props} />
      </ProgressGuard>
    </QueryProvider>
  );
}

export default ChangePhoneWithProgressGuard;
